/**
 * VerdictOverlay V5 — Full-viewport overlay with 5-stage timed reveal.
 * Shown after "Run Diagnosis" in the calculator wizard.
 *
 * Stages: 1=Hero card, 2=Arc gauge, 3=Legacy explainer, 4=Draggable corridor, 5=Teaser+Actions
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { T } from '@/styles/tokens';
import { DraggableCorridor } from './DraggableCorridor';
import { ForensicRoastTeaser } from './ForensicRoastTeaser';
import { PricePositionArc } from './PricePositionArc';
import { LegacyRangeExplainer } from './LegacyRangeExplainer';
import { computeLegacyRange } from '@/lib/legacyMethods';
import type { MediumKey } from '@/types/medium';

interface VerdictOverlayProps {
  visible: boolean;
  floorCents: number;
  priceCents: number;
  hours: number;
  materialsCents: number;
  medium: string;
  mediumKey: MediumKey;
  width: number;
  height: number;
  tier: string;
  effectiveHourly: number;
  survivalTarget: number;
  monthlyBurn: number;       // cents
  monthlyCapacity: number;   // hours
  isPremium: boolean;
  onAdjust: () => void;
  onSave: () => void;
  onPriceChange: (cents: number) => void;
  onClose: () => void;
  salesChannel?: string;
  channelLabel?: string;
  retailFloorCents?: number;
  retailCeilingCents?: number;
}

function getMarginSentiment(delta: number, floor: number) {
  if (delta <= 0) return { color: T.danger, bg: '#FEF2F2' };
  const pct = (delta / floor) * 100;
  if (pct >= 20) return { color: T.primary, bg: T.primarySubtle };
  if (pct >= 10) return { color: T.primary, bg: T.primarySubtle };
  return { color: T.warning, bg: '#FEF3EB' };
}

function getAuthority(delta: number, floor: number): string {
  if (delta < 0) {
    const pct = Math.round(Math.abs(delta / floor) * 100);
    if (pct > 30) return 'Pricing below floor. Every hour costs you money at this price.';
    return 'Below breakeven. Consider raising to cover costs.';
  }
  const pct = Math.round((delta / floor) * 100);
  if (pct >= 50) return 'Strong margin. Room to negotiate.';
  if (pct >= 20) return 'Healthy margin above breakeven.';
  if (pct >= 5) return 'Clearing your floor. Tight but defensible.';
  return 'At floor. No margin for negotiation.';
}

export function VerdictOverlay({
  visible, floorCents, priceCents, hours, materialsCents,
  medium, mediumKey, width, height, tier, effectiveHourly, survivalTarget,
  monthlyBurn, monthlyCapacity, isPremium,
  onAdjust, onSave, onPriceChange, onClose,
  salesChannel, channelLabel, retailFloorCents, retailCeilingCents,
}: VerdictOverlayProps) {
  const showRetail = salesChannel && salesChannel !== 'direct' && retailFloorCents && retailCeilingCents;
  const [revealStage, setRevealStage] = useState(0);
  const [localPriceCents, setLocalPriceCents] = useState(priceCents);

  // Sync external price changes
  useEffect(() => { setLocalPriceCents(priceCents); }, [priceCents]);

  const handlePriceChange = useCallback((cents: number) => {
    setLocalPriceCents(cents);
    onPriceChange(cents);
  }, [onPriceChange]);

  // Live derived values
  const liveDelta = localPriceCents - floorCents;
  const sentiment = getMarginSentiment(liveDelta, floorCents);
  const authority = getAuthority(liveDelta, floorCents);
  // survivalTarget prop is in $/hr (dollars), localPriceCents is in cents
  const ehDollars = hours > 0 ? Math.round(localPriceCents / hours / 100) : 0;
  const stDollars = Math.round(survivalTarget); // already in dollars
  const liveSustainability = stDollars > 0 ? ehDollars / stDollars : 0;

  // Legacy range
  const legacyRange = useMemo(() => computeLegacyRange({
    medium: mediumKey,
    width, height, hours,
    materials: materialsCents,
    monthlyBurn, monthlyCapacity,
  }), [mediumKey, width, height, hours, materialsCents, monthlyBurn, monthlyCapacity]);

  const scaleMax = useMemo(() => {
    const floorD = Math.round(floorCents / 100);
    const priceD = Math.round(localPriceCents / 100);
    const maxVal = Math.max(priceD, legacyRange.high, floorD) * 1.4;
    return Math.max(Math.ceil(maxVal / 100) * 100, 200);
  }, [floorCents, localPriceCents, legacyRange.high]);

  // Reveal timing
  useEffect(() => {
    if (!visible) { setRevealStage(0); return; }
    const timers = [
      setTimeout(() => setRevealStage(1), 100),
      setTimeout(() => setRevealStage(2), 600),
      setTimeout(() => setRevealStage(3), 1100),
      setTimeout(() => setRevealStage(4), 1600),
      setTimeout(() => setRevealStage(5), 2100),
    ];
    return () => timers.forEach(clearTimeout);
  }, [visible]);

  if (!visible) return null;

  const revealStyle = (stage: number): React.CSSProperties => ({
    opacity: revealStage >= stage ? 1 : 0,
    transform: revealStage >= stage ? 'translateY(0)' : 'translateY(8px)',
    transition: `all 400ms ${T.easeEntrance}`,
  });

  const floorDollars = Math.round(floorCents / 100);
  const priceDollars = Math.round(localPriceCents / 100);

  const cardStyle: React.CSSProperties = {
    backgroundColor: T.white, borderRadius: 20,
    border: `1px solid ${T.border}`, boxShadow: T.shadowRaised,
    padding: 20, overflow: 'hidden',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: T.cream, overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
    }}>
      <div style={{
        maxWidth: 430, margin: '0 auto', minHeight: '100dvh',
        display: 'flex', flexDirection: 'column' as const,
        fontFamily: T.fontFamily,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px' }}>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: '50%',
            border: `1.5px solid ${T.charcoal200}`, background: T.white,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', padding: 0, color: T.charcoal400,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 13L5 8l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.primary, textTransform: 'uppercase' as const, letterSpacing: '0.1em' }}>
            Price Check
          </div>
          <div style={{ width: 36 }} />
        </div>

        {/* Stage 1: Hero Card */}
        <div style={{ margin: '0 20px 16px', ...revealStyle(1) }}>
          <div style={{ ...cardStyle, borderRadius: 24, textAlign: 'center' as const }}>
            <div style={{
              fontSize: 52, fontWeight: 800, color: T.text,
              letterSpacing: '-0.04em', lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}>
              ${priceDollars.toLocaleString()}
            </div>

            {/* Delta chip */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 20, marginTop: 10,
              background: sentiment.bg,
            }}>
              {liveDelta > 0 && <TrendingUp size={14} color={sentiment.color} />}
              {liveDelta < 0 && <TrendingDown size={14} color={sentiment.color} />}
              {liveDelta === 0 && <Minus size={14} color={T.textMuted} />}
              <span style={{ fontSize: 13, fontWeight: 700, color: sentiment.color, fontVariantNumeric: 'tabular-nums' }}>
                {liveDelta === 0 ? 'At floor' : `${liveDelta > 0 ? '+' : ''}$${Math.abs(Math.round(liveDelta / 100)).toLocaleString()} ${liveDelta > 0 ? 'above' : 'below'}`}
              </span>
            </div>

            {/* Authority */}
            <p style={{
              fontSize: 13, fontWeight: 500, color: T.textSecondary,
              lineHeight: 1.5, marginTop: 12, maxWidth: 250,
              marginLeft: 'auto', marginRight: 'auto', marginBottom: 0,
            }}>
              {authority}
            </p>

            {/* Meta row */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
              marginTop: 20, paddingTop: 16,
              borderTop: `1px solid ${T.borderLight}`,
            }}>
              {[
                { label: 'Medium', value: medium },
                { label: 'Size', value: `${width}×${height}"` },
                { label: 'Hours', value: `${hours}h` },
                { label: 'Tier', value: tier },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' as const }}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: T.textMuted }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 2 }}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stage 2: Arc Gauge */}
        <div style={{ margin: '0 20px 16px', ...revealStyle(2) }}>
          <div style={cardStyle}>
            <PricePositionArc
              floorPrice={floorDollars}
              askingPrice={priceDollars}
              legacyLow={legacyRange.low}
              legacyHigh={legacyRange.high}
              medium={medium}
              dimensions={`${width}" × ${height}"`}
            />
          </div>
        </div>

        {/* Stage 3: Legacy Range */}
        <div style={{ margin: '0 20px 16px', ...revealStyle(3) }}>
          <div style={cardStyle}>
            <LegacyRangeExplainer
              methods={legacyRange.methods}
              floorPrice={floorDollars}
              askingPrice={priceDollars}
              scaleMax={scaleMax}
            />
          </div>
        </div>

        {/* Retail price (if non-direct channel) */}
        {showRetail && (
          <div style={{ margin: '0 20px 16px', ...revealStyle(3) }}>
            <div style={{ background: T.cream, borderRadius: 12, padding: '12px 16px', border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.charcoal300, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 6 }}>
                Retail Price (what the buyer pays)
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.charcoal600 }}>
                ${Math.round(retailFloorCents! / 100).toLocaleString()} – ${Math.round(retailCeilingCents! / 100).toLocaleString()}
              </div>
              <div style={{ fontSize: 12, color: T.charcoal400, marginTop: 4 }}>
                {channelLabel} — above is what you receive
              </div>
            </div>
          </div>
        )}

        {/* Stage 4: Corridor Card */}
        <div style={{ margin: '0 20px 16px', ...revealStyle(4) }}>
          <div style={cardStyle}>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, lineHeight: 1.45, margin: '0 0 16px' }}>
              Adjust your asking price by dragging the marker on the bar.
            </p>
            <DraggableCorridor
              floorCents={floorCents}
              priceCents={localPriceCents}
              onPriceChange={handlePriceChange}
            />
            {/* Inline stats */}
            <div style={{ display: 'flex', marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.borderLight}` }}>
              <div style={{ flex: 1, paddingRight: 12 }}>
                <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: T.textMuted }}>TIME VALUE</div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: sentiment.color, fontVariantNumeric: 'tabular-nums' }}>${ehDollars}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.charcoal400 }}>/hr</span>
                </div>
                <div style={{ fontSize: 10, color: T.charcoal300, marginTop: 2 }}>Target: ${stDollars}/hr</div>
              </div>
              <div style={{ width: 1, background: T.borderLight }} />
              <div style={{ flex: 1, paddingLeft: 12 }}>
                <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: T.textMuted }}>SUSTAINABILITY</div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: sentiment.color, fontVariantNumeric: 'tabular-nums' }}>{liveSustainability.toFixed(1)}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.charcoal400 }}>×</span>
                </div>
                <div style={{ fontSize: 10, color: T.charcoal300, marginTop: 2 }}>{liveSustainability >= 1 ? 'Above survival' : 'Below survival'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stage 5: Forensic Teaser */}
        <div style={{ margin: '0 20px 16px', ...revealStyle(5) }}>
          <ForensicRoastTeaser isPremium={isPremium} />
        </div>

        {/* Spacer */}
        <div style={{ flex: 1, minHeight: 100 }} />

        {/* Sticky action bar */}
        <div style={{
          position: 'sticky', bottom: 0,
          padding: '12px 20px',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          background: `linear-gradient(transparent, ${T.cream} 20%)`,
          opacity: revealStage >= 5 ? 1 : 0,
          transition: 'opacity 300ms ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={onAdjust} style={{
              background: 'none', border: 'none', padding: '8px 4px',
              fontSize: 13, fontWeight: 600, color: T.charcoal400,
              cursor: 'pointer', fontFamily: T.fontFamily,
              flexShrink: 0, whiteSpace: 'nowrap',
            }}>
              Adjust Inputs
            </button>
            <button onClick={onSave} style={{
              flex: 1, height: 52, borderRadius: 26, border: 'none',
              background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
              color: T.white, fontSize: 15, fontWeight: 700,
              cursor: 'pointer', fontFamily: T.fontFamily,
              boxShadow: `0 4px 16px ${T.primary}44`,
            }}>
              Save Price
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
